import React, { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import {
	WEEKDAYS,
	formatWeekdaysDisplay,
	formatWeekdaysShort,
} from "../../../utils/temporal.utils";
import {
	CmVisitScheduleOption,
	CutDay,
} from "../../../domain/treatmentPlan.interface";
import {
	CUT_DAY_NAMES,
	deriveCutDays,
	hasDerivableCutDays,
	nextOptionNumber,
	usesCutDays,
} from "./cutDays";

/**
 * Building a visit schedule: which days a participant attends, and which slice
 * of the week each check counts.
 *
 * Three things this deliberately does differently from the form it replaces:
 *
 * - The option NUMBER is assigned, not typed. It was a free-text field
 *   defaulting to 0, and 0 reads as "unset" everywhere it is used, so a
 *   template pointing at it could not be saved.
 * - Cut days are derived from the cadence and shown straight away. They used to
 *   sit below the fold behind an "Add Cut Day Pattern" button, so a schedule
 *   could be created without them — which silently stops missed entries being
 *   generated at all.
 * - They stay editable, because custom windows are a real thing people use.
 *   Derived is a starting point, not a cage.
 */

interface VisitScheduleEditorProps {
	options: CmVisitScheduleOption[];
	onChange: (options: CmVisitScheduleOption[]) => void;
}

const emptyDraft = (): {
	weekdays: number[];
	cutDays: CutDay[];
	touched: boolean;
} => ({ weekdays: [], cutDays: [], touched: false });

const VisitScheduleEditor: React.FC<VisitScheduleEditorProps> = ({
	options,
	onChange,
}) => {
	const [draft, setDraft] = useState(emptyDraft());
	const [isAdding, setIsAdding] = useState(false);

	const visitsPerWeek = draft.weekdays.length;
	const scheduleDays = useMemo(
		() => formatWeekdaysShort(draft.weekdays),
		[draft.weekdays],
	);

	const toggleWeekday = (value: number): void => {
		const weekdays = draft.weekdays.includes(value)
			? draft.weekdays.filter((day) => day !== value)
			: [...draft.weekdays, value].sort((a, b) => a - b);

		setDraft({
			weekdays,
			// Re-derive while the person has not hand-edited the windows. Once
			// they have, their edits survive a cadence change and the reset button
			// is how they go back.
			cutDays: draft.touched ? draft.cutDays : deriveCutDays(weekdays.length),
			touched: draft.touched,
		});
	};

	const editCutDay = (
		index: number,
		field: keyof CutDay,
		value: string,
	): void => {
		const cutDays = draft.cutDays.map((cutDay, i) =>
			i === index ? { ...cutDay, [field]: value } : cutDay,
		);
		setDraft({ ...draft, cutDays, touched: true });
	};

	const save = (): void => {
		onChange([
			...options,
			{
				cmVisitScheduleOptions: nextOptionNumber(options),
				cmVisitScheduleOptionsDays: scheduleDays,
				cmVisitScheduleOptionsDaysDisplay: formatWeekdaysDisplay(
					draft.weekdays,
					"full",
				),
				cutDays: draft.cutDays,
			},
		]);
		setDraft(emptyDraft());
		setIsAdding(false);
	};

	const remove = (index: number): void =>
		onChange(options.filter((_, i) => i !== index));

	const needsCutDays = usesCutDays(visitsPerWeek);
	const cutDaysMissing = needsCutDays && draft.cutDays.length === 0;

	return (
		<Box>
			<Typography variant="subtitle2" sx={{ mb: 1 }}>
				Visit Schedules
			</Typography>

			{options.length > 0 && (
				<Table size="small" sx={{ mb: 2 }} data-testid="schedule-list">
					<TableHead>
						<TableRow>
							<TableCell>#</TableCell>
							<TableCell>Visit days</TableCell>
							<TableCell>Counted periods</TableCell>
							<TableCell align="right" />
						</TableRow>
					</TableHead>
					<TableBody>
						{options.map((option, index) => (
							<TableRow key={option.cmVisitScheduleOptions}>
								<TableCell>{option.cmVisitScheduleOptions}</TableCell>
								<TableCell>
									{option.cmVisitScheduleOptionsDaysDisplay ||
										option.cmVisitScheduleOptionsDays}
								</TableCell>
								<TableCell>
									{option.cutDays.length === 0 ? (
										<Typography variant="body2" color="error">
											None — missed entries will not be generated
										</Typography>
									) : (
										option.cutDays
											.map(
												(cutDay) =>
													`${cutDay.runningday}: ${cutDay.startcutday}–${cutDay.endcutday}`,
											)
											.join("  ·  ")
									)}
								</TableCell>
								<TableCell align="right">
									<IconButton
										size="small"
										aria-label={`Remove schedule ${option.cmVisitScheduleOptions}`}
										onClick={() => remove(index)}
									>
										<DeleteIcon fontSize="small" />
									</IconButton>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			)}

			{!isAdding ? (
				<Button variant="outlined" onClick={() => setIsAdding(true)}>
					Add Visit Schedule
				</Button>
			) : (
				<Card variant="outlined" sx={{ p: 2 }}>
					<Typography variant="caption" display="block" sx={{ mb: 1 }}>
						Which days does the participant attend?
					</Typography>
					<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
						{WEEKDAYS.map((weekday) => (
							<Chip
								key={weekday.value}
								label={weekday.shortName}
								// Addressable on its own: the day names also appear as
								// options in the cut-day selects below, so matching on
								// text alone is ambiguous.
								data-testid={`weekday-${weekday.shortName}`}
								onClick={() => toggleWeekday(weekday.value)}
								color={
									draft.weekdays.includes(weekday.value) ? "primary" : "default"
								}
								variant={
									draft.weekdays.includes(weekday.value) ? "filled" : "outlined"
								}
							/>
						))}
					</Box>

					{visitsPerWeek > 0 && (
						<Typography variant="body2" sx={{ mb: 2 }}>
							{visitsPerWeek} visit{visitsPerWeek === 1 ? "" : "s"} a week
							{scheduleDays ? ` — ${scheduleDays}` : ""}
						</Typography>
					)}

					{needsCutDays && (
						<>
							<Typography variant="caption" display="block" sx={{ mb: 1 }}>
								Which stretch of the week each check counts. Filled in from the
								cadence — adjust if this program counts differently.
							</Typography>

							{cutDaysMissing && (
								<Alert severity="warning" sx={{ mb: 2 }}>
									{hasDerivableCutDays(visitsPerWeek)
										? "Pick the visit days to fill these in."
										: `There is no standard pattern for ${visitsPerWeek} visits a week, so these have to be set by hand. Without them no missed entries are generated.`}
								</Alert>
							)}

							{draft.cutDays.length > 0 && (
								<Table size="small" sx={{ mb: 2 }} data-testid="cut-day-rows">
									<TableHead>
										<TableRow>
											<TableCell>Checked on</TableCell>
											<TableCell>Counts from</TableCell>
											<TableCell>Counts to</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{draft.cutDays.map((cutDay, index) => (
											<TableRow key={cutDay.runningday}>
												<TableCell>{cutDay.runningday}</TableCell>
												{(["startcutday", "endcutday"] as const).map(
													(field) => (
														<TableCell key={field}>
															<TextField
																select
																size="small"
																// Native: a dense table of day pickers is
																// quicker to keyboard through than MUI's
																// popover, and it is what a screen reader
																// announces as a listbox.
																SelectProps={{ native: true }}
																value={cutDay[field]}
																inputProps={{
																	"aria-label": `${cutDay.runningday} ${field}`,
																}}
																onChange={(event) =>
																	editCutDay(index, field, event.target.value)
																}
															>
																{CUT_DAY_NAMES.map((name) => (
																	<option key={name} value={name}>
																		{name}
																	</option>
																))}
															</TextField>
														</TableCell>
													),
												)}
											</TableRow>
										))}
									</TableBody>
								</Table>
							)}

							{draft.touched && (
								<Button
									size="small"
									sx={{ mb: 2 }}
									onClick={() =>
										setDraft({
											...draft,
											cutDays: deriveCutDays(visitsPerWeek),
											touched: false,
										})
									}
								>
									Reset to the standard pattern
								</Button>
							)}
						</>
					)}

					<Box sx={{ display: "flex", gap: 1 }}>
						<Button
							variant="contained"
							disabled={visitsPerWeek === 0}
							onClick={save}
						>
							Save Schedule
						</Button>
						<Button
							onClick={() => {
								setDraft(emptyDraft());
								setIsAdding(false);
							}}
						>
							Cancel
						</Button>
					</Box>
				</Card>
			)}
		</Box>
	);
};

export default VisitScheduleEditor;
